import SliderCarousal from "../../components/Sliders/HeaderSlider/Index";
import AnimatedBoxes from "../../components/AnimatedBoxes";
import ServicesSection from "../../components/ServicesSection";
import ProductsSection from "../../components/ProductsSection";
import Clients from "./Clients";
import { useDocumentTitle } from "../../utils/data";
import { Box, Container, Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { keyframes } from "@mui/system";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-12px); }
`;

const rotateSlow = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const shimmerBg = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Home = () => {
  useDocumentTitle("Briozon - Home page");

  return (
    <>
      <SliderCarousal />
      <AnimatedBoxes />
      <ServicesSection />
      <ProductsSection />
      <Clients />

      {/* ── CTA Section ── */}
      <Box
        sx={{
          position: "relative",
          py: { xs: 10, sm: 14, md: 18 },
          overflow: "hidden",
          background: "linear-gradient(135deg, #0f0c29 0%, #302b63 40%, #764ba2 80%, #5b7cfa 100%)",
          backgroundSize: "300% 300%",
          animation: `${shimmerBg} 10s ease infinite`,
        }}
      >
        {/* Dot grid */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* Rotating rings */}
        {[
          { size: 560, top: "-200px", right: "-200px", dur: "35s", border: "rgba(91,124,250,0.2)", rev: false },
          { size: 400, top: "-140px", right: "-140px", dur: "22s", border: "rgba(118,75,162,0.2)", rev: true },
          { size: 260, bottom: "-80px", left: "-80px",  dur: "28s", border: "rgba(56,189,248,0.15)", rev: false },
        ].map((r, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: r.size,
              height: r.size,
              top: r.top,
              right: r.right,
              bottom: r.bottom,
              left: r.left,
              borderRadius: "50%",
              border: `1.5px solid ${r.border}`,
              animation: `${rotateSlow} ${r.dur} linear infinite ${r.rev ? "reverse" : ""}`,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        ))}

        {/* Floating orbs */}
        {[
          { size: 100, top: "15%",    left: "8%",   color: "rgba(91,124,250,0.15)",  delay: "0s",  dur: "7s" },
          { size: 70,  bottom: "20%", right: "12%", color: "rgba(118,75,162,0.15)", delay: "1s",  dur: "9s" },
          { size: 50,  top: "55%",    left: "35%",  color: "rgba(56,189,248,0.1)",  delay: "2s",  dur: "6s" },
        ].map((orb, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: orb.size,
              height: orb.size,
              top: orb.top,
              left: orb.left,
              right: orb.right,
              bottom: orb.bottom,
              borderRadius: "50%",
              background: orb.color,
              animation: `${floatY} ${orb.dur} ease-in-out infinite ${orb.delay}`,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        ))}

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            {/* Pill label */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2.5,
                py: 0.8,
                borderRadius: "999px",
                border: "1px solid rgba(255,255,255,0.25)",
                background: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(8px)",
                mb: 3.5,
                animation: `${fadeUp} 0.6s ease-out both`,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "#fff",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: "#fff",
                    animation: `${pulseRing} 1.8s ease-out infinite`,
                  },
                }}
              />
              <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 12, fontWeight: 700, letterSpacing: 1.5 }}>
                LET'S BUILD TOGETHER
              </Typography>
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "32px", sm: "46px", md: "60px" },
                fontWeight: 900,
                letterSpacing: "-1.5px",
                lineHeight: 1.1,
                color: "#fff",
                mb: 2.5,
                animation: `${fadeUp} 0.7s ease-out 0.1s both`,
              }}
            >
              Ready to{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg, #93c5fd, #c4b5fd, #f9a8d4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Grow Faster?
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                color: "rgba(255,255,255,0.7)",
                mb: 5,
                maxWidth: "640px",
                mx: "auto",
                lineHeight: 1.8,
                animation: `${fadeUp} 0.7s ease-out 0.2s both`,
              }}
            >
              We provide the solutions to grow your business faster. Let's turn your vision into reality — together.
            </Typography>

            {/* CTA Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: 2, sm: 3 },
                justifyContent: "center",
                flexWrap: "wrap",
                animation: `${fadeUp} 0.7s ease-out 0.3s both`,
              }}
            >
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                sx={{
                  background: "#fff",
                  color: "#5b7cfa",
                  fontWeight: 800,
                  px: { xs: 4, sm: 5 },
                  py: 1.7,
                  borderRadius: "14px",
                  fontSize: { xs: "15px", sm: "16px" },
                  textTransform: "none",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "#f0f4ff",
                    transform: "translateY(-4px)",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.3)",
                  },
                }}
              >
                Get In Touch
              </Button>

              <Button
                component={Link}
                to="/services"
                variant="outlined"
                sx={{
                  borderColor: "rgba(255,255,255,0.4)",
                  color: "#fff",
                  fontWeight: 700,
                  px: { xs: 4, sm: 5 },
                  py: 1.7,
                  borderRadius: "14px",
                  fontSize: { xs: "15px", sm: "16px" },
                  textTransform: "none",
                  backdropFilter: "blur(8px)",
                  background: "rgba(255,255,255,0.07)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.7)",
                    background: "rgba(255,255,255,0.15)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                Explore Services
              </Button>
            </Box>

            {/* Trust badges */}
            <Box
              sx={{
                mt: 6,
                display: "flex",
                gap: { xs: 2, sm: 4 },
                justifyContent: "center",
                flexWrap: "wrap",
                animation: `${fadeUp} 0.7s ease-out 0.4s both`,
              }}
            >
              {["✓  50+ Clients", "✓  10+ Years", "✓  100% Satisfaction"].map((badge, i) => (
                <Typography
                  key={i}
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: { xs: "13px", sm: "14px" },
                    fontWeight: 500,
                    letterSpacing: 0.3,
                  }}
                >
                  {badge}
                </Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Home;
